/**
 * Project AI refresh — reusable orchestrator for re-deriving project metadata.
 *
 * Surfaces the same logic in three places:
 *  - The "Regenerate with AI" button in Settings → project-general (preview-then-apply)
 *  - The one-click 🪄 button on the workspace header (apply directly)
 *  - The bulk refresh runner in Settings → app-projects (iterate every project)
 *
 * Name / description / icon come from the AI service (`generate_project_details`).
 * Run configs come from the deterministic `detectRunConfigurations` walker —
 * pattern-matching on package.json / Cargo.toml / etc. doesn't need an LLM
 * and is much faster.
 */
import type { IBackend } from '@/services/backend/IBackend';
import type { Project } from '@/services/backend/types';
import { detectRunConfigurations } from '@/lib/runConfigDetector';
import { logger } from '@/lib/logger';
import { useBulkRefreshStore } from '@/stores/bulkRefreshStore';
import { useProjectStore } from '@/stores/projectStore';
import { useUIStore } from '@/stores/uiStore';
import { showToast } from '@/components/ui/Toast';

const TAG = 'ProjectAIRefresh';

export interface RefreshFields {
  name?: boolean;
  description?: boolean;
  icon?: boolean;
  runConfigs?: boolean;
}

export const ALL_FIELDS: Required<RefreshFields> = {
  name: true,
  description: true,
  icon: true,
  runConfigs: true,
};

export interface RefreshResult {
  /** Fields that were actually changed (subset of `fields` requested). */
  applied: Array<keyof RefreshFields>;
  /** Fields that were skipped because the AI returned nothing useful. */
  skipped: Array<keyof RefreshFields>;
  /** Empty unless something failed. The refresh is best-effort per field. */
  errors: Array<{ field: keyof RefreshFields; message: string }>;
}

/**
 * Pre-flight check the caller can run once before a bulk refresh — returns
 * a human-readable error string if the AI provider isn't reachable. Lets the
 * UI fail fast with a clear message instead of running silently and producing
 * boilerplate fallback output.
 */
export async function checkAIServiceReady(backend: IBackend): Promise<string | null> {
  try {
    const ok = await backend.projects.isAIServiceAvailable();
    if (!ok) {
      return (
        "AI service unavailable — Claude CLI wasn't found on PATH. " +
        'Install with `npm install -g @anthropic-ai/claude-code` or set AI_PROVIDER to a configured provider.'
      );
    }
    return null;
  } catch {
    return 'AI service status check failed.';
  }
}

/**
 * Re-derive metadata for one project. Always best-effort: if the AI call
 * fails, the run-config pass still runs (and vice versa). Returns a per-field
 * breakdown so callers can surface a clear "applied / skipped / failed" UX.
 */
export async function refreshProjectMetadata(
  backend: IBackend,
  project: Project,
  fields: RefreshFields = ALL_FIELDS,
): Promise<RefreshResult> {
  const result: RefreshResult = { applied: [], skipped: [], errors: [] };
  const wantsAi = fields.name || fields.description || fields.icon;

  // ── AI-driven fields (name / description / icon) ──
  if (wantsAi) {
    try {
      const details = await backend.projects.generateDetails(project.root_path);
      const updates: { name?: string; prd_content?: string; icon?: string } = {};
      if (fields.name && details.name?.trim()) {
        updates.name = details.name.trim();
        result.applied.push('name');
      } else if (fields.name) {
        result.skipped.push('name');
      }
      if (fields.description && details.description?.trim()) {
        updates.prd_content = details.description.trim();
        result.applied.push('description');
      } else if (fields.description) {
        result.skipped.push('description');
      }
      if (fields.icon && details.icon?.trim()) {
        updates.icon = details.icon.trim();
        result.applied.push('icon');
      } else if (fields.icon) {
        result.skipped.push('icon');
      }

      if (Object.keys(updates).length > 0) {
        await backend.projects.update(project.id, updates);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      logger.warn(TAG, `AI generate failed for ${project.name}:`, e);
      // Mark every requested AI field as errored so the UX can show "AI step failed".
      if (fields.name) result.errors.push({ field: 'name', message });
      if (fields.description) result.errors.push({ field: 'description', message });
      if (fields.icon) result.errors.push({ field: 'icon', message });
    }
  }

  // ── Deterministic detection (run configs) ──
  if (fields.runConfigs) {
    try {
      const readFile = (path: string) => backend.files.readFile(project.id, path);
      const rootNodes = await backend.files.getFolderChildren(project.id, project.root_path);
      const detected = await detectRunConfigurations({
        readFile,
        rootFiles: rootNodes.map((n) => n.name),
      });

      if (detected.length > 0) {
        const runners = {
          $schema: 'https://ateliercode.dev/schemas/runners.v1.json',
          configs: detected.map((c) => ({ name: c.name, command: c.command })),
          default: detected[0]?.name ?? null,
        };
        await backend.files.writeAteliercodeRunners(project.id, JSON.stringify(runners, null, 2));
        result.applied.push('runConfigs');
      } else {
        result.skipped.push('runConfigs');
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      logger.warn(TAG, `Run config detection failed for ${project.name}:`, e);
      result.errors.push({ field: 'runConfigs', message });
    }
  }

  return result;
}

/**
 * Run `refreshProjectMetadata` against a list of projects sequentially.
 * Sequential (not parallel) so we don't hammer the AI rate limit and so
 * progress reporting is easy to display.
 */
export async function refreshManyProjects(
  backend: IBackend,
  projects: Project[],
  fields: RefreshFields,
  onProgress?: (done: number, total: number, current: Project) => void,
): Promise<Map<string, RefreshResult>> {
  const results = new Map<string, RefreshResult>();
  for (let i = 0; i < projects.length; i++) {
    const project = projects[i];
    onProgress?.(i, projects.length, project);
    try {
      results.set(project.id, await refreshProjectMetadata(backend, project, fields));
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      results.set(project.id, {
        applied: [],
        skipped: [],
        errors: [
          { field: 'name', message },
          { field: 'description', message },
          { field: 'icon', message },
          { field: 'runConfigs', message },
        ],
      });
    }
  }
  onProgress?.(projects.length, projects.length, projects[projects.length - 1]);
  return results;
}

/**
 * Fire-and-forget orchestrator for the Settings → Project Maintenance bulk
 * refresh. Updates `useBulkRefreshStore` so the floating pill shows progress
 * even after the user closes the modal, mirrors per-project name/icon back
 * into `useProjectStore` (so the sidebar updates live), and pops a clickable
 * toast on completion that deep-links Settings back to the maintenance pane.
 *
 * Caller does NOT await — kicking the runner off and returning immediately
 * is the whole point. The store + toast carry the UX.
 */
export function startBulkRefreshInBackground(
  backend: IBackend,
  projects: Project[],
  fields: RefreshFields,
): void {
  if (projects.length === 0) return;
  const { status, start, updateProgress, finish } = useBulkRefreshStore.getState();
  if (status === 'running') {
    logger.warn(TAG, 'Refusing to start bulk refresh: another run is already in progress');
    return;
  }

  start(projects.length, projects[0].name, fields);

  void (async () => {
    try {
      const results = await refreshManyProjects(backend, projects, fields, (done, _total, current) => {
        updateProgress(done, current.name);
      });

      // Mirror name/icon updates back into the project store so the sidebar
      // reflects them without a full reload.
      const updateInStore = useProjectStore.getState().updateProject;
      for (const project of projects) {
        try {
          const fresh = await backend.projects.get(project.id);
          if (fresh) {
            await updateInStore(project.id, {
              name: fresh.name,
              prd_content: fresh.prd_content ?? undefined,
              icon: fresh.icon ?? undefined,
            });
          }
        } catch {
          /* DB write already committed — best-effort sidebar sync */
        }
      }

      let succeeded = 0;
      let skipped = 0;
      let failed = 0;
      for (const r of results.values()) {
        if (r.errors.length > 0 && r.applied.length === 0) failed += 1;
        else if (r.applied.length === 0) skipped += 1;
        else succeeded += 1;
      }
      finish(succeeded, skipped, failed);

      const toastKind = failed > 0 ? 'error' : 'success';
      const summary = `Project refresh done — ${succeeded} updated`
        + (skipped > 0 ? `, ${skipped} skipped` : '')
        + (failed > 0 ? `, ${failed} failed` : '');
      showToast(summary, toastKind, {
        action: {
          label: 'View',
          onClick: () => useUIStore.getState().openSettingsTo('app-projects'),
        },
      });
    } catch (e) {
      logger.warn(TAG, 'Bulk refresh crashed', e);
      finish(0, 0, projects.length);
      showToast('Bulk refresh failed — see logs.', 'error');
    }
  })();
}
